<script setup>
/* eslint-disable no-console */
import { watch, computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const router = useRouter();

const contactId = computed(() => props.contactId);
const isLoading = ref(false);
const orders = ref([]);

const fetchOrders = async () => {
  if (!contactId.value) {
    orders.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const response = await ServiceOrdersAPI.get({
      contact_id: contactId.value,
    });
    orders.value = response.data.payload || [];
  } catch (error) {
    console.error('Error fetching contact orders:', error);
    orders.value = [];
  } finally {
    isLoading.value = false;
  }
};

const getStatusClass = status => {
  const classes = {
    draft: 'bg-gray-100 text-gray-800',
    ready: 'bg-yellow-100 text-yellow-800',
    dispatched: 'bg-blue-100 text-blue-800',
    en_route: 'bg-violet-100 text-violet-800',
    on_site: 'bg-blue-100 text-blue-800',
    field_complete: 'bg-green-100 text-green-800',
    redirected: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-red-100 text-red-800',
    merged: 'bg-gray-100 text-gray-800',
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const formatStatus = status => {
  return (
    status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) ||
    'Unknown'
  );
};

const viewOrder = orderId => {
  router.push({
    name: 'orders_list',
    query: { order_id: orderId },
  });
};

watch(
  contactId,
  newContactId => {
    if (newContactId) {
      fetchOrders();
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="orders.length"
      class="flex flex-col max-h-[300px] overflow-y-auto"
    >
      <div
        v-for="order in orders"
        :key="order.id"
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 cursor-pointer"
        @click="viewOrder(order.id)"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            <span
              class="inline-flex px-1 py-0.5 mr-1 text-[10px] font-semibold rounded"
              :class="
                order.order_class === 'service_order'
                  ? 'bg-n-teal-9/10 text-n-teal-11'
                  : 'bg-n-brand/10 text-n-blue-text'
              "
            >
              {{ order.order_class === 'service_order' ? 'OS' : 'OT' }}
            </span>
            Orden #{{ order.id }} - {{ order.order_type }}
          </span>
          <span
            class="inline-flex w-fit px-2 py-0.5 text-xs font-medium rounded-full"
            :class="getStatusClass(order.status)"
          >
            {{ formatStatus(order.status) }}
          </span>
        </div>
        <i class="i-lucide-chevron-right text-n-slate-11 flex-shrink-0" />
      </div>
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      No hay órdenes de servicio para este contacto
    </p>
  </div>
</template>
